import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  ListItem,
} from '@mui/material';
import { usePlatformFeatures } from '../hooks/usePlatformFeatures';
import { PlatformFeatureDto } from '../api/platformApi';
import {
  useSetGlobalOverride,
  useRemoveGlobalOverride,
} from '../hooks/useFeatureOverrideMutations';
import FeatureToggleConfirmDialog, {
  FeatureToggleAction,
  OverrideState,
} from '../components/FeatureToggleConfirmDialog';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';

type OverrideFilter = 'all' | 'overridden' | 'default';
type CategoryFilter =
  | 'all'
  | 'Development'
  | 'Operational'
  | 'Rollout'
  | 'Research'
  | 'PayGated';

const OVERRIDE_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Overridden', value: 'overridden' },
  { label: 'No Override', value: 'default' },
] as const;

const CATEGORY_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Development', value: 'Development' },
  { label: 'Operational', value: 'Operational' },
  { label: 'Rollout', value: 'Rollout' },
  { label: 'Research', value: 'Research' },
  { label: 'Pay Gated', value: 'PayGated' },
] as const;

const CATEGORY_COLORS: Record<
  string,
  'info' | 'warning' | 'default' | 'secondary' | 'primary'
> = {
  Development: 'info',
  Operational: 'warning',
  Rollout: 'default',
  Research: 'secondary',
  PayGated: 'primary',
};

function getOverrideChip(globalOverride: PlatformFeatureDto['globalOverride']) {
  switch (globalOverride) {
    case 'enabled':
      return { label: 'Enabled', color: 'success' as const };
    case 'disabled':
      return { label: 'Disabled', color: 'error' as const };
    default:
      return { label: 'Default (off)', color: 'default' as const };
  }
}

function toOverrideState(
  globalOverride: PlatformFeatureDto['globalOverride']
): OverrideState {
  return globalOverride ?? 'default';
}

function getGlobalAction(feature: PlatformFeatureDto): FeatureToggleAction {
  const currentState = toOverrideState(feature.globalOverride);
  if (feature.globalOverride !== null) {
    return { type: 'remove', scope: 'global', currentState };
  }
  return { type: 'set', scope: 'global', currentState };
}

const PlatformFeatureSwitchesPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [overrideFilter, setOverrideFilter] = useState<OverrideFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [selectedFeature, setSelectedFeature] =
    useState<PlatformFeatureDto | null>(null);

  const {
    data: features,
    isLoading,
    error,
  } = usePlatformFeatures(appliedSearch || undefined);

  const setGlobalOverride = useSetGlobalOverride();
  const removeGlobalOverride = useRemoveGlobalOverride();

  const isMutating =
    setGlobalOverride.isPending || removeGlobalOverride.isPending;

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setAppliedSearch(value);
  };

  const filteredFeatures = useMemo(() => {
    let items = features ?? [];
    if (overrideFilter === 'overridden')
      items = items.filter(f => f.globalOverride !== null);
    else if (overrideFilter === 'default')
      items = items.filter(f => f.globalOverride === null);
    if (categoryFilter !== 'all')
      items = items.filter(f => f.category === categoryFilter);
    return items;
  }, [features, overrideFilter, categoryFilter]);

  const handleRowClick = useCallback((feature: PlatformFeatureDto) => {
    setSelectedFeature(feature);
  }, []);

  const handleConfirm = useCallback(
    (isEnabled: boolean) => {
      if (!selectedFeature) return;

      const action = getGlobalAction(selectedFeature);
      const onSuccess = () => setSelectedFeature(null);

      if (action.type === 'remove') {
        removeGlobalOverride.mutate(
          { key: selectedFeature.key },
          { onSuccess }
        );
      } else {
        setGlobalOverride.mutate(
          { key: selectedFeature.key, isEnabled },
          { onSuccess }
        );
      }
    },
    [selectedFeature, setGlobalOverride, removeGlobalOverride]
  );

  const handleCancel = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  if (isLoading && !features) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box>
          <PageHeader title="Feature Switches" />

          <PageToolbar
            tabs={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ChipTabs
                  tabs={[...CATEGORY_TABS]}
                  value={categoryFilter}
                  onChange={v => setCategoryFilter(v as CategoryFilter)}
                  data-testid="category-filter-tabs"
                />
                <ChipTabs
                  tabs={[...OVERRIDE_TABS]}
                  value={overrideFilter}
                  onChange={v => setOverrideFilter(v as OverrideFilter)}
                  data-testid="override-filter-tabs"
                />
              </Box>
            }
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
          />

          <ListViewShell
            itemCount={filteredFeatures.length}
            loading={isLoading}
            emptyMessage="No feature switches found"
            data-testid="platform-features-list"
            columnHeaders={
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                >
                  Feature
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    width: 200,
                    textAlign: 'right',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  Global State
                </Typography>
              </>
            }
          >
            {filteredFeatures.map(feature => {
              const chipProps = getOverrideChip(feature.globalOverride);
              const categoryColor =
                CATEGORY_COLORS[feature.category] ?? 'default';
              return (
                <ListItem
                  key={feature.key}
                  data-testid="platform-feature-row"
                  onClick={() => handleRowClick(feature)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: { xs: 2, sm: 3 },
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {feature.key}
                      </Typography>
                      <Chip
                        label={feature.category}
                        color={categoryColor}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 120,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Chip
                      label={chipProps.label}
                      color={chipProps.color}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  </Box>
                </ListItem>
              );
            })}
          </ListViewShell>
        </Box>
      </Box>

      {selectedFeature && (
        <FeatureToggleConfirmDialog
          open={!!selectedFeature}
          featureKey={selectedFeature.key}
          featureDescription={selectedFeature.description}
          action={getGlobalAction(selectedFeature)}
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </Box>
  );
};

export default PlatformFeatureSwitchesPage;
